<template>
    <div class="row">
        <div class="col-lg-12">
            <InternalProductMainInformationPage
                ref="mainProductInformation"
                :product="product"
                :relations="relations"
                @update="updateProduct"
                @duplicate="duplicateProduct"
                @delete="deleteProduct"
            />
        </div>
        <div class="col-lg-2" v-show="hasCustomFields">
            <InternalProductCustomFieldsPage :product="product" @count="hasCustomFields = $event > 0" />
        </div>
        <div :class="hasCustomFields ? 'col-lg-10' : 'col-lg-12'">
            <NavTab class="nav-tabs-bottom">
                <NavTabItem key="#publications" :active="isActiveTab(['PublicationsPage'])" @click="changePage('PublicationsPage')" href="#">
                    <i class="icon-repo-forked mr-2"></i>
                    {{ $t('labels.publications') }}
                </NavTabItem>
                <NavTabItem key="#prices" :active="isActiveTab(['InternalProductPricesPage'])" @click="changePage('InternalProductPricesPage')" href="#">
                    <i class="icon-price-tag mr-2"></i>
                    {{ $t('sidebar.items.prices') }}
                </NavTabItem>
                <NavTabItem key="#availability" :active="isActiveTab(['InternalProductAvailabilityPage'])" @click="changePage('InternalProductAvailabilityPage')" href="#">
                    <i class="icon-truck mr-2"></i>
                    {{ $t('sidebar.items.availability') }}
                </NavTabItem>
                <NavTabItem key="#recommendations" :active="isActiveTab(['InternalProductRecommendationsPage'])" @click="changePage('InternalProductRecommendationsPage')" href="#">
                    <i class="icon-thumbs-up2 mr-2"></i>
                    {{ $t('sidebar.items.recommendations') }}
                </NavTabItem>
                <NavTabItem v-if="groupKey" key="#groupProducts" :active="isActiveTab(['InternalProductGroupProductsPage'])" @click="changePage('InternalProductGroupProductsPage')" href="#">
                    <i class="icon-copy mr-2"></i>
                    {{ $t('sidebar.items.groupProducts') }}
                </NavTabItem>
            </NavTab>
            <router-view v-slot="{ Component }">
                <component
                    v-if="product && Component"
                    :is="Component"
                    v-bind="getComponentProps(Component)"
                >
                </component>
            </router-view>
        </div>
    </div>
</template>
<script>
import InternalProductMainInformationPage from "./pages/InternalProductMainInformationPage.vue";
import NavTab from "../../../components/tabs/NavTab.vue";
import NavTabItem from "../../../components/tabs/NavTabItem.vue";
import InternalProductCustomFieldsPage from "./pages/InternalProductCustomFieldsPage.vue";

import { httpClient } from "../../../../services/HttpClient";
import { mapGetters } from "vuex";
import MessageNotificator from "../../../../services/MessageNotificator";
import HttpSender from "../../../../services/Http/HttpSender";
import HttpRequest from "../../../../services/Http/HttpRequest";

export default {
    name: 'ProductEditPage',
    components: {
        InternalProductMainInformationPage, NavTab, NavTabItem, InternalProductCustomFieldsPage
    },
    data() {
        return {
            product: null,
            relations: [],
            groupKey: null,
            priceTemplates: [],
            hasCustomFields: false
        }
    },
    methods: {
        changePage(pageName) {
            this.$router.push({name: pageName})
        },
        isActiveTab(routeNames) {
            const isActive = routeNames.includes(this.$route.name)

            return isActive
        },
        getComponentProps(component) {
            const name = component.type.name;

            switch (name) {
                case 'InternalProductPriceTemplatesPage':
                    return { product: this.product, priceTemplates: this.priceTemplates };
                case 'InternalProductGroupProductsPage':
                    return { groupKey: this.groupKey };
                default:
                    return { product: this.product };
            }
        },
        fetchPriceTemplates() {
            httpClient.get(this.priceTemplatesUrl, {
                limit: 0
            }).then((response) => {
                this.priceTemplates = response.data.data
            })
        },
        async fetchProduct() {
            await httpClient.get(`/catalog/product-items/${this.$route.params.productUuid}`).then((response) => {
                this.product = response.data.data
                this.groupKey = response.data.data.group_key
            })
            this.fetchRelations()
        },
        updateProduct() {
            let requestData = {
                name: this.product.name,
                sku: this.product.sku,
                automatic_arrival: this.product.automatic_arrival,
                category_uuid: this.product.category?.uuid ?? null,
                measurement: this.product.measurement,
                manufacturer: this.product.manufacturer,
                group_key: this.product.group_key ?? null,
                cost: this.product.cost,
                cost_currency_uuid: this.product.cost_currency?.uuid ?? null
            };

            const mainInformation = this.$refs.mainProductInformation;
            const relations = mainInformation.mainImageRemoved && !mainInformation.newMainImage
                ? this.relations.filter((relation) => relation.uuid !== this.product.main_image_uuid)
                : this.relations;

            requestData.files = relations.map((relation) => {
                if (relation.uuid !== undefined) {
                    return {
                        relationship_uuid: relation.uuid
                    }
                } else if (relation.file.uuid) {
                    return {
                        file_uuid: relation.file.uuid
                    }
                }
            })

            if (mainInformation.newMainImage) {
                requestData.files.unshift({
                    file_uuid: mainInformation.newMainImage.uuid
                })
            }

            requestData.custom_fields = Object.entries(this.product.custom_fields).map(([uuid, value]) => ({uuid, value}))
            requestData.price_templates = this.priceTemplates
                .filter((priceTemplate) => this.product.price_templates[priceTemplate.uuid] !== null && this.product.price_templates[priceTemplate.uuid] !== '' && this.product.price_templates[priceTemplate.uuid] !== undefined)
                .map((priceTemplate) => ({uuid: priceTemplate.uuid, value: Number(this.product.price_templates[priceTemplate.uuid])}))

            httpClient.patch(`/catalog/product-items/${this.product.uuid}`, requestData).then((response) => {
                this.product = response.data.data
                this.groupKey = response.data.data.group_key
                this.fetchRelations()
                new MessageNotificator().success(this.$t("alerts.product_updated"))
            })
        },
        async duplicateProduct() {
            let requestData = {
                name: this.product.name,
                automatic_arrival: this.product.automatic_arrival
            };

            const timestamp = Date.now().toString();
            const lastThreeDigits = timestamp.slice(-3);

            requestData.sku = this.product.sku + '-DUPLICATE-' + lastThreeDigits;

            const mapObjectToArray = (object) => Object.entries(object).map(([uuid, value]) => ({uuid, value}))

            if (this.product.custom_fields && Object.keys(this.product.custom_fields).length > 0) {
                requestData.custom_fields = mapObjectToArray(this.product.custom_fields)
            }

            if (this.product.price_templates && Object.keys(this.product.price_templates).length > 0) {
                requestData.price_templates = this.priceTemplates
                    .filter((priceTemplate) => this.product.price_templates[priceTemplate.uuid] !== null && this.product.price_templates[priceTemplate.uuid] !== '' && this.product.price_templates[priceTemplate.uuid] !== undefined)
                    .map((priceTemplate) => ({uuid: priceTemplate.uuid, value: this.product.price_templates[priceTemplate.uuid]}))
            }

            if (this.product.category_uuid) {
                requestData.category_uuid = this.product.category_uuid
            }

            if (this.product.manufacturer) {
                requestData.manufacturer = this.product.manufacturer
            }

            if (this.product.measurement) {
                requestData.measurement = this.product.measurement
            }

            if (this.product.group_id) {
                requestData.group_id = this.product.group_id
            }

            await httpClient.post('/catalog/product-items', requestData).then(async (response) => {
                let filesConnectRequests = this.relations.map((relation) => {
                    if (relation.origin_url || relation.file.url) {
                        let body = {
                            model_type: "product_item",
                            model_uuid: response.data.data.uuid,
                        };

                        if (relation.origin_url) {
                            body.origin_url = relation.origin_url;
                        }

                        if (relation.file?.url) {
                            body.file_uuid = relation.file.uuid;
                        }

                        return new HttpRequest('POST', '/file/file-relationships', body);
                    }
                });

                await new HttpSender().sendRequestsInBatches(filesConnectRequests, () => {})

                this.$router.push({ name: "EditProductPage", params: {
                    projectUuid: this.project.uuid,
                    productUuid: response.data.data.uuid
                }})
            })
        },
        deleteProduct() {
            new MessageNotificator().confirm(this.$t('alerts.confirm_delete', {
                value: this.$t('labels.product')
            }), () => {
                httpClient.delete(`/catalog/product-items/${this.product.uuid}`).then(() => {
                    this.$router.push({ name: "ProductsIndexPage", params: {
                        projectUuid: this.project.uuid
                    }})
                })
            })
        },
        fetchRelations() {
            httpClient.get('/file/file-relationships', {
                model_types: ['product_item'],
                model_uuids: [this.product.uuid],
                orders: [{
                    column: "id",
                    dir: "asc"
                }],
                limit: 0
            }).then((response) => {
                this.relations = response.data.data.sort((a, b) => {
                    if (a.uuid === this.product.main_image_uuid) {
                        return -1;
                    } else if (b.uuid === this.product.main_image_uuid) {
                        return 1;
                    }
                });
            });
        }
    },
    computed: {
        priceTemplatesUrl() {
            return `/projects/${this.$route.params.projectUuid}/pim/price-templates`;
        },
        ...mapGetters('route', ['project'])
    },
    watch: {
        "$route.params.productUuid": {
            immediate: true,
            handler() {
                this.fetchProduct();
            },
        },
    },
    mounted() {
        this.fetchPriceTemplates()
    }
}
</script>
